import React, { useEffect, useState } from 'react';
// Redux
import { useDispatch, useSelector } from 'react-redux';
import { holdTemplateNames } from '../../../../Store/Redux/FileListSlice';
// API
import { fetchTemplates, createTemplate, updateTemplate } from '../../../../Services/APIConfig';
// MUI
import { Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import PrimaryButton from '../../../Common/Button/PrimaryButton';
import { useNavigate } from 'react-router-dom';
import GenerateThumbnail from '../../../../utils/GenerateThumbnail';
import Loading from '../../../Common/LoaderComponent/Loader';

const CKTemplatePopup = ({ open, onClose, type }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [localTemplateName, setLocalTemplateName] = useState('');
  const [initialTemplateName, setInitialTemplateName] = useState('');
  const [cancelClickedOnce, setCancelClickedOnce] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [responseReceived, setResponseReceived] = useState(false);
  const { selectedFileStatus, holdTemplateName } = useSelector(state => state.fileList);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (responseReceived) setResponseReceived(false);

    if (type !== 'create' && holdTemplateName !== null) {
      setInitialTemplateName(holdTemplateName);
      setLocalTemplateName(holdTemplateName);
    } else setLocalTemplateName('');
  }, [type, holdTemplateName, responseReceived]);

  const handleUpdateAPI = async fileName => {
    const Response = await fetchTemplates(1, 100);
    const isNameAlreadyPresent = Response.data.some(file => file.templateName === fileName);
    const isNameTooLong = fileName.length >= 16;

    if (isNameAlreadyPresent) {
      setErrorMessage('Template name already present, please try with a different name.');
      setCancelClickedOnce(false);
      return false;
    }

    if (isNameTooLong) {
      setErrorMessage('Please provide a name less than or equal to 16 characters.');
      setCancelClickedOnce(false);
      return false;
    }

    setErrorMessage('');
    return true;
  };

  const handleTemplateAction = async () => {
    setLoading(true);
    try {
      const isValid = await handleUpdateAPI(localTemplateName);

      if (!isValid) return;

      if (type === 'create') {
        const templateThubnail = await GenerateThumbnail(selectedFileStatus?.templateHtml);
        const data = {
          templateName: localTemplateName,
          templateThumbnail: templateThubnail || '',
          templateHtml: selectedFileStatus?.templateHtml,
          templatePdf: selectedFileStatus?.templatePdf,
          templateConfig: selectedFileStatus?.templateConfig,
          createdBy: selectedFileStatus?.createdBy,
          templateFilePath: selectedFileStatus?.templateFilePath,
          stampConfig: selectedFileStatus?.stampConfig
        };

        const Response = await createTemplate(data);

        if (Response.status === 200) {
          setResponseReceived(true);
          onClose();
          navigate(`/edit?createmode=true&fileId=${Response.templateId}`); // Testing
        } else setErrorMessage('An error occurred while processing the API request.');
      } else if (type === 'edit') {
        if (holdTemplateName === localTemplateName) {
          setErrorMessage('Name present, please try with a different name');
        } else if (selectedFileStatus === null) {
          dispatch(holdTemplateNames(localTemplateName));
          onClose(false);
        } else {
          const data = {
            templateId: selectedFileStatus.templateId,
            templateName: localTemplateName
          };
          const Response = await updateTemplate(data.templateId, data);

          if (Response.status === 200) {
            setResponseReceived(true);
            dispatch(holdTemplateNames(localTemplateName));
            onClose(false);
          } else setErrorMessage('An error occurred while processing the API request.');
        }
      }
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelTemplate = () => {
    if (cancelClickedOnce) {
      onClose(false);
      setCancelClickedOnce(false);
      setErrorMessage('');
      setLocalTemplateName(initialTemplateName);
      setResponseReceived(false);
    } else {
      setCancelClickedOnce(true);
      setErrorMessage('Are you sure you want to cancel? Click again to navigate back.');
    }
  };

  const handleCloseTemplate = () => {
    onClose(false);
    setCancelClickedOnce(false);
    setErrorMessage('');
    setLocalTemplateName('');
    setResponseReceived(false);
  };

  const cancelButtonText = cancelClickedOnce ? 'ReCancel' : 'Cancel';

  return (
    <>
      <Dialog
        open={open}
        PaperProps={{
          style: {
            width: '312px',
            borderRadius: '8px'
          }
        }}
      >
        {responseReceived ? null : (
          <DialogTitle>{type === 'create' ? 'Enter Copy Template Name' : 'Edit Template Name'}</DialogTitle>
        )}
        <DialogContent>
          {responseReceived ? (
            <Typography color='success'>
              {type === 'create'
                ? `Template data created copy name with ${localTemplateName}. Close the view to see the new template.`
                : `Template name changed from ${selectedFileStatus.templateName} to ${localTemplateName}.`}
            </Typography>
          ) : (
            <>
              <TextField
                autoFocus
                margin='dense'
                placeholder={type === 'create' ? 'Enter Copy Template Name' : 'Edit Template Name'}
                InputProps={{ style: { borderRadius: '8px' } }}
                maxLength={30}
                fullWidth
                value={localTemplateName}
                onChange={e => setLocalTemplateName(e.target.value)}
              />
              {errorMessage && <Typography color='error'>{errorMessage}</Typography>}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {responseReceived ? (
            <PrimaryButton
              value='Close'
              onClick={handleCloseTemplate}
              color='primary'
              backgroundColor='#B7D423'
              padding='10px 30px'
              borderRadius='8px'
            />
          ) : (
            <>
              <PrimaryButton
                value={cancelButtonText}
                onClick={handleCancelTemplate}
                textcolor='#B7D423'
                padding='10px 30px'
                backgroundColor='transparent'
                borderRadius='8px'
                border='2px solid #B7D423'
              />
              <PrimaryButton
                value='Save'
                onClick={handleTemplateAction}
                color='primary'
                disabled={!localTemplateName || localTemplateName === initialTemplateName}
                backgroundColor='#B7D423'
                padding='10px 30px'
                borderRadius='8px'
              />
            </>
          )}
        </DialogActions>
      </Dialog>
      {loading && <Loading open={loading} onClose={() => setLoading(false)} />}
    </>
  );
};

export default CKTemplatePopup;
